import { i18n, MessageDescriptor } from "@lingui/core";
import { defineMessage, t } from "@lingui/core/macro";
import { sortBy } from "lodash/fp";
import { DateTime, Duration, LocaleOptions } from "luxon";

import { DEFAULT_LANGUAGE_CODE } from "@readai/common/constants/locales";

export const formatTimeTick = (ms: number): string => {
  const duration = Duration.fromMillis(Math.abs(ms));
  let timeString;
  if (duration.shiftTo("hours").get("hours") >= 1) {
    timeString = duration.toFormat("h:mm:ss");
  } else {
    timeString = duration.toFormat("m:ss");
  }
  return ms < 0 ? `-${timeString}` : timeString;
};

export const TWO_MINUTES_MS = 2 * 60 * 1000;

export const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const ONE_HOUR_MS = 60 * 60 * 1000;

export const EIGHT_HOURS_DURATION = Duration.fromObject({ hours: 8 });

/**
 * @param startTime Time at which the other time is relative to
 * @param time Time for which to create the relative timestamp
 * @return ms The millisecond difference between the two times
 * */
export const msDurationBetween = (
  startTime: DateTime,
  time: DateTime,
): number => {
  return time.diff(startTime).toMillis();
};

export const getFormattedTimestampFromMs = (millis: number) => {
  if (millis > ONE_HOUR_MS) {
    return Duration.fromMillis(millis).toFormat("h:mm:ss");
  }
  return Duration.fromMillis(Math.max(millis, 0)).toFormat("m:ss");
};

/**
 * @param startTime Time at which the other time is relative to
 * @param time Time for which to create the relative timestamp
 * @return timestamp Formatted timestamp of relative time (e.g. "1:34")
 * */
export const getRelativeTimestamp = (startTime: DateTime, time: DateTime) => {
  const msDiff = msDurationBetween(startTime, time);
  return getFormattedTimestampFromMs(msDiff);
};

const AFTERNOON_CUTOFF = 12; // 12PM
const EVENING_CUTOFF = 17; // 5PM

/**
 * Gets a humanized greeting based on time of day
 * @return greeting (e.g., "Good morning", "Good afternoon", "Good evening")
 */
export const getGreeting = ({
  time,
  name,
}: {
  time?: DateTime<boolean>;
  name?: string;
} = {}) => {
  const effectiveTime = time ?? DateTime.local();
  const currentHour = effectiveTime.hour;

  if (currentHour >= AFTERNOON_CUTOFF && currentHour < EVENING_CUTOFF) {
    if (name) {
      return t({
        message: `Good afternoon, ${name}`,
        comment: "Afternoon greeting to given name",
      });
    }
    return t`Good afternoon`;
  }

  if (currentHour >= EVENING_CUTOFF) {
    if (name) {
      return t({
        message: `Good evening, ${name}`,
        comment: "Evening greeting to given name",
      });
    }
    return t`Good evening`;
  }

  if (name) {
    return t({
      message: `Good morning, ${name}`,
      comment: "Morning greeting to given name",
    });
  }

  return t`Good morning`;
};

/**
 * @param dateTime DateTime object to be formatted
 * @param locale Language locale
 * @return formatted locale string
 * */
export const formatTime = (
  dateTime: DateTime,
  locale: string = DEFAULT_LANGUAGE_CODE,
): string => {
  return dateTime.toLocaleString(DateTime.TIME_SIMPLE, { locale });
};

/**
 * Extracts keys from T whose values are either string, DateTime, or number.
 */
type TimeSortable = string | DateTime | number;
type ValidDateKey<T> = {
  [K in keyof T]: T[K] extends TimeSortable ? K : never;
}[keyof T];

const getTime = (value: TimeSortable) => {
  if (typeof value === "string") {
    return DateTime.fromISO(value).toMillis();
  }
  if (value instanceof DateTime) {
    return value.toMillis();
  }
  return value;
};

/**
 * Sorts a list of objects by a specified date key.
 *
 * @template T - The type of objects in the list.
 * @param {T[]} list - The list of objects to sort.
 * @param {ValidDateKey<T>} key - The key to sort by. The value associated with this key should be a string, DateTime, or number.
 * @param {"asc" | "desc"} [direction="asc"] - The direction to sort: "asc" for ascending or "desc" for descending.
 * @returns {T[]} The sorted list of objects.
 * @throws {Error} If the value associated with the key is not a string, DateTime, or number.
 */
export const sortByDate = <
  T extends object,
  K extends ValidDateKey<T> = ValidDateKey<T>,
>(
  list: T[] | undefined | null,
  key: K,
  direction: "asc" | "desc" = "desc",
): T[] =>
  (list ?? []).sort((a, b) => {
    const timeA = getTime(a[key] as TimeSortable);
    const timeB = getTime(b[key] as TimeSortable);

    return direction === "asc" ? timeA - timeB : timeB - timeA;
  });

/**
 * Create a formatted date string from a single DateTime object or a DateTime range.
 *
 * e.g. "Mon, Jan 1 • 12:00 PM - 1:00 PM"
 *      "Mon, Jan 1 2023 12:00 PM"
 *      "Mon, Jan 1 12:00 PM - Tue, Jan 2 1:00 PM"
 *
 * @param start Start DateTime object
 * @param end End DateTime object
 * @param options Luxon LocaleOptions
 * @returns formatted date string
 */
export const formatDateTimeRange = (
  start: DateTime,
  end?: DateTime | null,
  options?: LocaleOptions,
) => {
  const isSameYearAsNow = start.hasSame(DateTime.now(), "year");

  const startDateOptions: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: isSameYearAsNow ? undefined : "numeric",
    hour: "numeric",
    minute: "numeric",
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
  };

  if (!end) {
    return start.toLocaleString(startDateOptions, options);
  }

  const areSameDay = start.hasSame(end, "day");

  if (!areSameDay) {
    return `${start.toLocaleString(startDateOptions, options)} - ${end.toLocaleString(startDateOptions, options)}`;
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: isSameYearAsNow ? undefined : "numeric",
  };

  return `${start.toLocaleString(dateOptions, options)} • ${start.toLocaleString(timeOptions, options)} - ${end.toLocaleString(timeOptions, options)}`;
};

/**
 * Splits an array of objects into two buckets based on a DateTime property.
 * @param items Array of objects to be filtered.
 * @param dateTimeProp Name of the property in each object that holds a DateTime value.
 * @param splitPoint The DateTime point to split the array.
 * @returns An object containing two arrays: `olderOrSame` and `younger`.
 */
export const splitByDateTime = <T>(
  items: T[],
  dateTimeProp: keyof T,
  splitPoint: DateTime,
): { olderOrSame: T[]; younger: T[] } => {
  const olderOrSame = items.filter((item) => {
    const itemDateTime =
      typeof item[dateTimeProp] === "string"
        ? DateTime.fromISO(item[dateTimeProp])
        : (item[dateTimeProp] as DateTime);
    return itemDateTime <= splitPoint;
  });

  const younger = items.filter((item) => {
    const itemDateTime =
      typeof item[dateTimeProp] === "string"
        ? DateTime.fromISO(item[dateTimeProp])
        : (item[dateTimeProp] as DateTime);
    return itemDateTime > splitPoint;
  });

  return { olderOrSame, younger };
};

export const getFormattedDate = (
  date: DateTime | undefined,
  opts: LocaleOptions,
  showWeekday?: boolean,
) =>
  date?.toLocaleString(
    {
      weekday: showWeekday ? "short" : undefined,
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    opts,
  );

/**
 * Converts a Luxon DateTime to a rounded-up relative time string.
 *
 * This function calculates the precise difference between the provided DateTime and the current time,
 * then rounds up for time measurements greater than seconds, without crossing incorrect boundaries.
 *
 * @param dateTime - The Luxon DateTime instance to compare against the current time.
 * @returns A string representing the rounded-up relative time (e.g., "2 hours ago", "1 day ago").
 */
export const toRelativeRounded = (dateTime: DateTime): string => {
  const now = DateTime.now();

  // To account for skew, a time difference in either direction of less than 60 seconds
  // is considered "just now"
  const diffInSeconds = Math.abs(now.diff(dateTime, "seconds").seconds);

  if (diffInSeconds < 60) {
    return t`just now`;
  }

  if (dateTime > now) {
    return t`in the future`;
  }

  const minutes = Math.round(diffInSeconds / 60);
  if (minutes < 60) {
    return t`${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  }

  const hours = Math.round(diffInSeconds / 3600);
  if (hours < 24) {
    return t`${hours} hour${hours > 1 ? "s" : ""} ago`;
  }

  const days = Math.round(diffInSeconds / 86400);
  if (days < 30) {
    return t`${days} day${days > 1 ? "s" : ""} ago`;
  }

  const months = Math.round(diffInSeconds / 2592000);
  if (months < 12) {
    return t`${months} month${months > 1 ? "s" : ""} ago`;
  }

  const years = Math.round(diffInSeconds / 31536000);
  return t`${years} year${years > 1 ? "s" : ""} ago`;
};

/* eslint-disable lingui/no-unlocalized-strings */
const TIMEZONES: { [key: string]: MessageDescriptor } = {
  "Africa/Abidjan": defineMessage({
    message: "Africa - Abidjan",
    comment: "timezone location",
  }),
  "Africa/Accra": defineMessage({
    message: "Africa - Accra",
    comment: "timezone location",
  }),
  "Africa/Addis_Ababa": defineMessage({
    message: "Africa - Addis Ababa",
    comment: "timezone location",
  }),
  "Africa/Cairo": defineMessage({
    message: "Africa - Cairo",
    comment: "timezone location",
  }),
  "Africa/Casablanca": defineMessage({
    message: "Africa - Casablanca",
    comment: "timezone location",
  }),
  "Africa/Johannesburg": defineMessage({
    message: "Africa - Johannesburg",
    comment: "timezone location",
  }),
  "Africa/Lagos": defineMessage({
    message: "Africa - Lagos",
    comment: "timezone location",
  }),
  "Africa/Nairobi": defineMessage({
    message: "Africa - Nairobi",
    comment: "timezone location",
  }),
  "America/Anchorage": defineMessage({
    message: "America - Anchorage",
    comment: "timezone location",
  }),
  "America/Argentina/Buenos_Aires": defineMessage({
    message: "America - Argentina - Buenos Aires",
    comment: "timezone location",
  }),
  "America/Bogota": defineMessage({
    message: "America - Bogota",
    comment: "timezone location",
  }),
  "America/Caracas": defineMessage({
    message: "America - Caracas",
    comment: "timezone location",
  }),
  "America/Chicago": defineMessage({
    message: "America - Chicago",
    comment: "timezone location",
  }),
  "America/Denver": defineMessage({
    message: "America - Denver",
    comment: "timezone location",
  }),
  "America/Guatemala": defineMessage({
    message: "America - Guatemala",
    comment: "timezone location",
  }),
  "America/Halifax": defineMessage({
    message: "America - Halifax",
    comment: "timezone location",
  }),
  "America/Los_Angeles": defineMessage({
    message: "America - Los Angeles",
    comment: "timezone location",
  }),
  "America/Mexico_City": defineMessage({
    message: "America - Mexico City",
    comment: "timezone location",
  }),
  "America/New_York": defineMessage({
    message: "America - New York",
    comment: "timezone location",
  }),
  "America/Phoenix": defineMessage({
    message: "America - Phoenix",
    comment: "timezone location",
  }),
  "America/Santiago": defineMessage({
    message: "America - Santiago",
    comment: "timezone location",
  }),
  "America/Sao_Paulo": defineMessage({
    message: "America - Sao Paulo",
    comment: "timezone location",
  }),
  "America/St_Johns": defineMessage({
    message: "America - St Johns",
    comment: "timezone location",
  }),
  "America/Toronto": defineMessage({
    message: "America - Toronto",
    comment: "timezone location",
  }),
  "America/Vancouver": defineMessage({
    message: "America - Vancouver",
    comment: "timezone location",
  }),
  "America/Winnipeg": defineMessage({
    message: "America - Winnipeg",
    comment: "timezone location",
  }),
  "Asia/Bangkok": defineMessage({
    message: "Asia - Bangkok",
    comment: "timezone location",
  }),
  "Asia/Dhaka": defineMessage({
    message: "Asia - Dhaka",
    comment: "timezone location",
  }),
  "Asia/Dubai": defineMessage({
    message: "Asia - Dubai",
    comment: "timezone location",
  }),
  "Asia/Hong_Kong": defineMessage({
    message: "Asia - Hong Kong",
    comment: "timezone location",
  }),
  "Asia/Jakarta": defineMessage({
    message: "Asia - Jakarta",
    comment: "timezone location",
  }),
  "Asia/Karachi": defineMessage({
    message: "Asia - Karachi",
    comment: "timezone location",
  }),
  "Asia/Kolkata": defineMessage({
    message: "Asia - Kolkata",
    comment: "timezone location",
  }),
  "Asia/Kuala_Lumpur": defineMessage({
    message: "Asia - Kuala Lumpur",
    comment: "timezone location",
  }),
  "Asia/Seoul": defineMessage({
    message: "Asia - Seoul",
    comment: "timezone location",
  }),
  "Asia/Singapore": defineMessage({
    message: "Asia - Singapore",
    comment: "timezone location",
  }),
  "Asia/Tokyo": defineMessage({
    message: "Asia - Tokyo",
    comment: "timezone location",
  }),
  "Asia/Yangon": defineMessage({
    message: "Asia - Yangon",
    comment: "timezone location",
  }),
  "Atlantic/Cape_Verde": defineMessage({
    message: "Atlantic - Cape Verde",
    comment: "timezone location",
  }),
  "Australia/Adelaide": defineMessage({
    message: "Australia - Adelaide",
    comment: "timezone location",
  }),
  "Australia/Brisbane": defineMessage({
    message: "Australia - Brisbane",
    comment: "timezone location",
  }),
  "Australia/Darwin": defineMessage({
    message: "Australia - Darwin",
    comment: "timezone location",
  }),
  "Australia/Perth": defineMessage({
    message: "Australia - Perth",
    comment: "timezone location",
  }),
  "Australia/Sydney": defineMessage({
    message: "Australia - Sydney",
    comment: "timezone location",
  }),
  "Europe/Amsterdam": defineMessage({
    message: "Europe - Amsterdam",
    comment: "timezone location",
  }),
  "Europe/Athens": defineMessage({
    message: "Europe - Athens",
    comment: "timezone location",
  }),
  "Europe/Berlin": defineMessage({
    message: "Europe - Berlin",
    comment: "timezone location",
  }),
  "Europe/Brussels": defineMessage({
    message: "Europe - Brussels",
    comment: "timezone location",
  }),
  "Europe/Istanbul": defineMessage({
    message: "Europe - Istanbul",
    comment: "timezone location",
  }),
  "Europe/Lisbon": defineMessage({
    message: "Europe - Lisbon",
    comment: "timezone location",
  }),
  "Europe/London": defineMessage({
    message: "Europe - London",
    comment: "timezone location",
  }),
  "Europe/Moscow": defineMessage({
    message: "Europe - Moscow",
    comment: "timezone location",
  }),
  "Europe/Paris": defineMessage({
    message: "Europe - Paris",
    comment: "timezone location",
  }),
  "Europe/Rome": defineMessage({
    message: "Europe - Rome",
    comment: "timezone location",
  }),
  "Europe/Stockholm": defineMessage({
    message: "Europe - Stockholm",
    comment: "timezone location",
  }),
  "Pacific/Auckland": defineMessage({
    message: "Pacific - Auckland",
    comment: "timezone location",
  }),
  "Pacific/Fiji": defineMessage({
    message: "Pacific - Fiji",
    comment: "timezone location",
  }),
  "Pacific/Honolulu": defineMessage({
    message: "Pacific - Honolulu",
    comment: "timezone location",
  }),
  "Pacific/Marquesas": defineMessage({
    message: "Pacific - Marquesas",
    comment: "timezone location",
  }),
  "Pacific/Noumea": defineMessage({
    message: "Pacific - Noumea",
    comment: "timezone location",
  }),
  "Pacific/Pago_Pago": defineMessage({
    message: "Pacific - Pago Pago",
    comment: "timezone location",
  }),
};

export const getTimezoneName = (zone: string) => {
  const msgDescriptor = TIMEZONES[zone];
  const name = i18n._(msgDescriptor);
  const offset = parseInt(DateTime.local({ zone }).toFormat("Z"));
  return `${name} (UTC${offset >= 0 ? "+" : ""}${offset})`;
};

let timezoneObjs: { value: string; label: string; offset: number }[] = [];

export const getLocalizedTimezones = () => {
  if (timezoneObjs.length) {
    return timezoneObjs;
  }

  timezoneObjs = sortBy(
    "offset",
    Object.keys(TIMEZONES).map((zone) => {
      const offset = parseInt(DateTime.local({ zone }).toFormat("Z"));
      return {
        value: zone,
        label: getTimezoneName(zone),
        offset,
      };
    }),
  );

  return timezoneObjs;
};
/* eslint-enable lingui/no-unlocalized-strings */
